import React, { useContext, useMemo } from 'react';
import { Grid, Typography } from '@mui/material';
import { ParametersContext } from 'components/ParametersGuard';
import ClaimsTable from 'components/claims/ClaimsPage/ClaimsTable';
import { Claim } from 'store/types/Claim';
import ClaimsTableType from 'store/enums/ClaimsTableType';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';

import styles from 'components/claims/ClaimsPage/ClaimsPage.module.scss';

interface Props {
  historicClaims?: Claim[];
  companyId?: string;
  loading?: boolean;
}

const HistoricClaimsTab: React.FunctionComponent<Props> = ({ historicClaims, companyId, loading }) => {
  const {
    claims: { historicMyClaimsTableHeader, historicClaimsAgainstMeTableHeader },
  } = useContext(ParametersContext);

  const sortedMyClaims = useMemo(() => {
    return (
      historicClaims?.slice().sort((a, b) => {
        if (a.type !== b.type) return a.type < b.type ? -1 : 1;
        const aTime = a.dateClosed ? new Date(a.dateClosed).getTime() : 0;
        const bTime = b.dateClosed ? new Date(b.dateClosed).getTime() : 0;
        return bTime - aTime;
      }) ?? []
    );
  }, [historicClaims]);

  const myClaims = useMemo(
    () => sortedMyClaims.filter((c) => c.debtorCompanyId !== companyId),
    [companyId, sortedMyClaims]
  );

  const claimsAgainstMe = useMemo(
    () => sortedMyClaims.filter((c) => c.debtorCompanyId === companyId),
    [companyId, sortedMyClaims]
  );

  return (
    <Grid {...defaultGridContainerProps}>
      <Grid {...defaultGridItemProps}>
        <Typography variant="h2" className={styles.tableTitle}>
          {'Filed'}
        </Typography>
        {historicMyClaimsTableHeader && <p dangerouslySetInnerHTML={{ __html: historicMyClaimsTableHeader }} />}
        <ClaimsTable
          tableType={ClaimsTableType.MyClaims}
          historic={true}
          data={myClaims}
          loading={loading}
          preserveInitialOrder={true}
        />
      </Grid>
      <Grid {...defaultGridItemProps}>
        <Typography variant="h2" className={styles.tableTitle}>
          {'Filed Against Me'}
        </Typography>
        {historicClaimsAgainstMeTableHeader && (
          <p dangerouslySetInnerHTML={{ __html: historicClaimsAgainstMeTableHeader }} />
        )}
        <ClaimsTable
          tableType={ClaimsTableType.ClaimsAgainstMe}
          historic={true}
          data={claimsAgainstMe}
          loading={loading}
          preserveInitialOrder={true}
        />
      </Grid>
    </Grid>
  );
};

export default HistoricClaimsTab;
